import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { RouteFallback } from '@/components/common/RouteFallback';
import { ROUTES } from '@/constants/routes';
/**
 * Deep import: `@/features/files` is a lazy route chunk, and importing its
 * barrel here would pull the whole Drive page into the initial bundle.
 */
import { useContentSession } from '@/features/files/hooks/useContentSession';
import { isDesktopCallWindow } from '@/features/desktop/callWindow';
import { CallWindowSignedOut } from '@/features/desktop/components/CallWindowSignedOut';
import { useAuth } from '../hooks/useAuth';
import { ForcePasswordChange } from './ForcePasswordChange';

/**
 * Route guard. Wrap the authenticated route tree in it.
 *
 * While `status` is `checking` it renders the normal route fallback rather than
 * redirecting: a reload has no access token in memory yet, and bouncing to the
 * sign-in page before the refresh cookie has been tried would sign users out
 * every time they hit F5.
 */
export function RequireAuth() {
  const { status, mustChangePassword } = useAuth();
  const location = useLocation();

  // Opens the httpOnly ticket that lets the BROWSER fetch attachment bytes.
  // It belongs here rather than on the Files page because inline images render
  // in chat, channels and search long before anybody opens Drive.
  useContentSession(status === 'authenticated' && !mustChangePassword);

  if (status === 'checking') return <RouteFallback />;

  if (status === 'anonymous') {
    // A DESKTOP CALL WINDOW MUST NOT REACH THE SIGN-IN FORM. Every window in
    // that shell shares one cookie jar, so `POST /auth/login` from here writes
    // a fresh `cxt_rt` over the workspace's — a different account's, if a
    // different account is used — and the workspace's next rotation would
    // quietly continue as somebody else. The shell blocks the request outright
    // (main/authBroker.ts), so the form could only ever fail; the window closes
    // instead, and signing in belongs in the workspace window.
    if (isDesktopCallWindow()) return <CallWindowSignedOut />;
    // `from` survives the round trip so people land back where they aimed.
    return <Navigate to={ROUTES.login} replace state={{ from: location }} />;
  }

  // Rendered IN PLACE of the route tree rather than redirected to, on purpose:
  // the user keeps the url they aimed for and lands on it the moment the change
  // succeeds, and there is no route a determined click could reach around the
  // interruption. The API refuses that session everywhere regardless — this is
  // the instruction, not the enforcement.
  if (mustChangePassword) return <ForcePasswordChange />;

  return <Outlet />;
}
